<template>
    <div v-if="options" @keydown.enter.prevent.stop="submitForm()" class="account mrg" data-mrgt="40">
        <div class="login">
            <div class="form" novalidate>
                <fieldset class="fieldset mrg" data-mrgt="20" :disabled="isLoading">
                    <div class="form-items cf">
                        <div v-if="options.isUserName" class="item">
                            <div class="control">
                                <label class="label" for="UserName">{{ localizer.get("Account.Common.UserName") }}</label>
                                <input type="text" id="UserName" v-model.lazy="formData.userName" autocomplete="username" :class="[{ 'input-validation-error': isValidationError('UserName') }, 'textbox']" />
                                <span class="valid-sign">*</span>
                            </div>
                        </div>
                        <div class="item">
                            <div class="control">
                                <label class="label" for="Email">{{ localizer.get("Account.Common.Email") }}</label>
                                <input type="email" id="Email" v-model.lazy="formData.email" autocomplete="email" :class="[{ 'input-validation-error': isValidationError('Email') }, 'textbox']" />
                                <span class="valid-sign">*</span>
                            </div>
                        </div>
                        <div class="item">
                            <div class="control" data-type="password">
                                <label class="label" for="Password">{{ localizer.get("Account.Common.Password") }}</label>
                                <input type="password" id="Password" v-model.lazy="formData.password" autocomplete="off" :class="[{ 'input-validation-error': isValidationError('Password') }, 'textbox']" />
                                <button class="show-password" @click="formHelper.showPassword('Password'), showPasswordIcon()">
                                    <span v-if="isHidden" class="icon fastic-eye-show"></span>
                                    <span v-else class="icon fastic-eye-hide"></span>
                                </button>
                                <span class="valid-sign">*</span>
                            </div>
                        </div>
                        <div v-if="options.isPasswordCheck" class="item">
                            <div class="control" data-type="password">
                                <label class="label" for="ConfirmPassword">{{ localizer.get("Account.Common.PasswordCheck") }}</label>
                                <input type="password" id="ConfirmPassword" v-model.lazy="formData.passwordCheck" autocomplete="off" :class="[{ 'input-validation-error': isValidationError('ConfirmPassword') }, 'textbox']" />
                                <button class="show-password" @click="formHelper.showPassword('ConfirmPassword'), showPasswordIconCheck()">
                                    <span v-if="isHiddenCheck" class="icon fastic-eye-show"></span>
                                    <span v-else class="icon fastic-eye-hide"></span>
                                </button>
                                <span class="valid-sign">*</span>
                            </div>
                        </div>
                    </div>
                </fieldset>
                <fieldset class="fieldset" :disabled="isLoading">
                    <div class="form-items" data-type="privacy">
                        <div class="item">
                            <div class="checkbox">
                                <input type="checkbox" id="IsPrivacyPolicy" v-model="formData.isPrivacyPolicy" />
                                <label for="IsPrivacyPolicy">{{ localizer.get("Coupon.Common.PrivacyDisclaimer_Text") }} <span class="disclaimer"><a :href="routes.Terms_PrivacyPolicy" target="_blank" class="button-privacy">{{ localizer.get("Coupon.Common.PrivacyDisclaimer_Button") }}</a></span></label>
                            </div>
                        </div>
                    </div>
                </fieldset>
                <fieldset class="fieldset" :disabled="isLoading">
                    <div v-if="validationErrors.length !== 0" class="validation-summary-errors">
                        <ul>
                            <li v-for="(validationError, index) in validationErrors" :key="index">{{ validationError.message }}</li>
                        </ul>
                    </div>
                    <div class="form-items" data-type="buttons">
                        <div class="item calltoaction">
                            <button id="ButtonSubmit" class="button" @click="submitForm()" :disabled="isLoading"><span class="lable">{{ localizer.get("Account.Common.Button_Next") }}</span></button>
                        </div>
                    </div>
                    <div class="item">
                        <div class="captcha-terms" v-html="localizer.get('App.Recaptcha.CaptchaTerms')"></div>
                    </div>
                </fieldset>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount } from "vue";
import Axios from "axios";
import FormHelper from "@Scripts/Common/FormHelper";
import Localizer from "@Scripts/Common/Localizer";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";

const props = defineProps<{
    recaptchaSiteKey?: string,
    returnUrl?: string
}>();

const formHelper = FormHelper;
const localizer = Localizer;
const routes = (window as any).App.routes;

const isLoading = ref(false);
const isSuccess = ref(false);
const openPrivacy = ref(false);
const formData = ref<any>({});
const validationErrors = ref<any[]>([]);
const options = ref<any>(null);
const isHidden = ref(true);
const isHiddenCheck = ref(true);

onBeforeMount(async () => {
    options.value = (await Axios.post(routes.Account_GetOptions, null, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
});

async function submitForm() {
    FormHelper.removeFocus();

    validationErrors.value = [];

    isLoading.value = true;

    formData.value.redirectUrl = props.returnUrl;

    (window as any).grecaptcha.enterprise.ready(async () => {
        const token = await (window as any).grecaptcha.enterprise.execute(props.recaptchaSiteKey, { action: 'Submit' });
        try {
            const response = await Axios.post(routes.Account_Register, formData.value, { headers: { "RecaptchaVerificationToken": token } });

            isSuccess.value = true;

            GTagHelper.sendEvent("form_submit", "account_login");

            window.location.href = response.data.redirectUrl ? response.data.redirectUrl : routes.Home_Home;
        } catch (error: any) {
            if (error.response && error.response.status === 400) {
                validationErrors.value = FormHelper.getValidationErrors(error.response.data);
            }
        }

        isLoading.value = false;
    });
}

function showPasswordIcon() {
    isHidden.value = !isHidden.value;
}

function showPasswordIconCheck() {
    isHiddenCheck.value = !isHiddenCheck.value;
}

function isValidationError(controlName: string): boolean {
    return FormHelper.isControlValidationError(controlName, validationErrors.value);
}
</script>